// taken from here
// https://ionicacademy.com/ionic-http-interceptor/

// core
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, throwError, EMPTY, OperatorFunction } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

// campfire
import { LoggerService } from '@campfire-nx/api';

import { INLINE_HANDLING_FOR_SERVER_ERROR } from '../constants/inline-handling-for-server-error.constants';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  apiRequestTimeout: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loggerService: LoggerService
  ) { }

  // Intercepts all HTTP requests!
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = sessionStorage.getItem('token');
    const clonedReq = this.addToken(request, token);
    this.apiRequestTimeout = 30000;

    return next.handle(clonedReq).pipe(
      timeout(this.apiRequestTimeout) as OperatorFunction<HttpEvent<any>, HttpEvent<any>>,
      catchError((error: HttpErrorResponse): Observable<any | null | undefined> => {
        if (this.checkExcludedServerUrls(request.url)) {
          return throwError(error);
        }

        // we should not let logger bring down the app
        if (error.url) {
          if (
            error.url.indexOf('amazonaws') !== -1 ||
            error.url.indexOf('huddletogether') !== -1 ||
            error.url.indexOf('zdassets') !== -1
          ) {
            // this was fun trying to figure out this value
            return EMPTY;
          }
        }

        if (error.status === 403 || error.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          this.loggerService.info('AuthInterceptor', 'intercept', { error: error });
          this.router.navigate(['/unauthorised']);
        } else if (error.status === 404) {
          this.loggerService.warn('AuthInterceptor', 'intercept', { error: error });
          this.router.navigate(['/not-found']);
        } else if (error.status >= 500) {
          this.loggerService.error('AuthInterceptor', 'intercept', { error: error });
          // Scenario of Inter server errors
          this.router.navigate(['/bad-request']);
        } else if (error.status === 0) {
          // Scenario when Huddle server cannot be connected
          this.loggerService.error('AuthInterceptor', 'intercept', { error: error });
        } else if (error.name.toLowerCase() === 'timeouterror') {
          // Scenario when Huddle server is connected but request takes longer than 'apiRequestTimeout'
          this.loggerService.error('AuthInterceptor', 'intercept', { error: error });
        }
        // Pass the error to the caller of the function
        return throwError(error);
      })
    );
  }

  // Adds the token to your headers if it exists
  private addToken(request: HttpRequest<any>, token: any) {
    // we don't want that the token be included in amazonaws and validate email api calls
    if (token && request.url.indexOf('amazonaws') === -1  && request.url.indexOf('validate') === -1) {
      let clone: HttpRequest<any>;
      clone = request.clone({
        setHeaders: {
          Accept: `application/json`,
          'Content-Type': `application/json`,
          Authorization: `JWT ${token}`
        }
      });
      return clone;
    }
    return request;
  }

  // There are some APIs for which we would NOT want to redirect to server error page,
  //   because it may be possible to render the page and show some inline error message.
  // All such APIs should be listed in constants INLINE_HANDLING_FOR_SERVER_ERROR. Redirect user to
  //   server error page only if request URL is NOT in this list.
  // Before a new API is added in the list INLINE_HANDLING_FOR_SERVER_ERROR,
  //   make sure proper error handling is in place to show the error message in the UI.
  private checkExcludedServerUrls(requestUrl: string) {
    var shouldDisplayServerErrorPage = false;
    for (const exceptionUrl of INLINE_HANDLING_FOR_SERVER_ERROR) {
      if (requestUrl.indexOf(exceptionUrl) > -1) {
        shouldDisplayServerErrorPage = true;
      }
    }
    return shouldDisplayServerErrorPage;
  }
}
